<!--
 * 文件名：TagInput.vue
 * 描述：标签输入组件（参考八医院项目 hospitalInPc TagInput.vue 改造为 Vue2 + Element UI 版），
 *       支持回车添加标签、删除标签、拖拽排序；失焦时自动提交未落地的输入（否则用户不按回车
 *       直接点保存会丢输入，触发"请添加标签名称"的误报）
-->
<template>
  <div class="tag-input">
    <draggable
      v-model="tagList"
      ghost-class="tag-ghost"
      handle=".tag-drag-handle"
      class="tag-list"
      @end="syncToParent"
    >
      <el-tag
        v-for="(tag, index) in tagList"
        :key="index"
        closable
        :disable-transitions="false"
        :class="['tag-item', 'tag-drag-handle']"
        @close="handleClose(index)"
      >{{ tag }}</el-tag>
    </draggable>
    <el-input
      v-if="tagList.length < max || (preservePendingInput && inputValue)"
      v-model="inputValue"
      :type="batch ? 'textarea' : 'text'"
      :autosize="batch ? { minRows: 2, maxRows: 4 } : false"
      :resize="batch ? 'none' : undefined"
      :maxlength="batch ? undefined : maxLength"
      :placeholder="batch ? '单个直接输入，批量用英文分号分隔，回车添加' : '按回车键添加标签'"
      size="small"
      class="tag-editor"
      :class="{ 'tag-editor-batch': batch }"
      @input="notifyPending"
      @compositionstart.native="onCompositionStart"
      @compositionend.native="onCompositionEnd"
      @keydown.native="onKeyDown"
      @keyup.enter.native="handleEnter"
      @blur="handleEnter"
    ></el-input>
    <div class="tag-tip">按回车键添加{{ word }}，最多添加{{ max }}个<template v-if="maxLength">，单个{{ word }}不超过{{ maxLength }}字</template></div>
  </div>
</template>

<script>
import draggable from "vuedraggable";

export default {
  name: "TagInput",
  components: { draggable },
  props: {
    // 标签数组（v-model）
    value: {
      type: Array,
      default: () => [],
    },
    // 单个标签最大字数
    maxLength: {
      type: Number,
      default: 0,
    },
    // 最多标签数
    max: {
      type: Number,
      default: 20,
    },
    // 仅需要批量录入的页面启用；其他调用保持原有单项输入行为
    batch: {
      type: Boolean,
      default: false,
    },
    // 数量上限变化时保留尚未添加的输入，供调用方引导用户处理。
    preservePendingInput: {
      type: Boolean,
      default: false,
    },
    // 禁止标签包含的字符集（落库以某分隔符合并的场景须配置，如标签名称以竖线合并传 ["|"]）
    forbidChars: {
      type: Array,
      default: () => [],
    },
    // 禁用字符在提示文案中的称呼（如"竖线"），未配置时直接展示命中字符
    forbidName: {
      type: String,
      default: "",
    },
    // 名词定制（如"选项"），用于提示与校验文案；默认"标签"
    word: {
      type: String,
      default: "标签",
    },
  },
  data() {
    return {
      inputValue: "",
      tagList: [],
      composing: false,
      compositionEnter: false,
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.tagList = [...(val || [])];
      },
    },
  },
  beforeDestroy() {
    if (this.batch) this.$emit("pending-change", { pending: false, input: this });
  },
  methods: {
    notifyPending(value) {
      if (this.batch) this.$emit("pending-change", { pending: this.composing || !!value, input: this });
    },
    onCompositionStart() {
      if (!this.batch) return;
      this.composing = true;
      this.notifyPending(true);
    },
    onCompositionEnd(event) {
      if (!this.batch) return;
      this.composing = false;
      this.notifyPending(this.inputValue || (event.target && event.target.value));
    },
    onKeyDown(event) {
      if (!this.batch) return;
      if (event.key === "Enter" || event.keyCode === 13 || event.keyCode === 229) {
        this.compositionEnter = this.composing || event.isComposing || event.keyCode === 229;
        if (!this.compositionEnter && (event.key === "Enter" || event.keyCode === 13)) {
          event.preventDefault();
        }
      }
    },
    /** 回车添加标签 */
    handleEnter(event) {
      if (this.batch) {
        if (this.composing || (event && event.type === "keyup" &&
          (event.isComposing || event.keyCode === 229 || this.compositionEnter))) {
          this.compositionEnter = false;
          return;
        }
        this.compositionEnter = false;
        this.handleBatchInput(event);
        return;
      }
      const value = this.inputValue.trim();
      if (!value) {
        return;
      }
      if (this.tagList.length >= this.max) {
        this.$message.warning(`最多添加${this.max}个${this.word}`);
        return;
      }
      if (this.maxLength && value.length > this.maxLength) {
        this.$message.warning(`单个${this.word}不超过${this.maxLength}个字`);
        return;
      }
      // 禁用字符拦截：任一命中即拒绝（提示文案随 prop 输出，如"标签名称不能包含竖线"）
      const forbidHit = (this.forbidChars || []).find((c) => value.includes(c));
      if (forbidHit !== undefined) {
        this.$message.warning(`${this.word}不能包含${this.forbidName || forbidHit}`);
        return;
      }
      if (this.tagList.includes(value)) {
        this.$message.warning(`${this.word}已存在`);
        return;
      }
      this.tagList.push(value);
      this.inputValue = "";
      this.syncToParent();
    },
    handleBatchInput(event) {
      if (!this.inputValue) {
        if (event && event.type === "keyup") this.$message.warning(`请输入有效${this.word}`);
        return;
      }
      const parts = this.inputValue.split(";").map((value) => value.trim());
      const values = parts.filter(Boolean);
      if (!values.length) {
        this.$message.warning(`请输入有效${this.word}`);
        return;
      }
      const seen = new Set(this.tagList);
      for (const value of values) {
        if (this.maxLength && value.length > this.maxLength) {
          this.$message.warning(`单个${this.word}不超过${this.maxLength}个字：${value}`);
          return;
        }
        const forbidHit = (this.forbidChars || []).find((c) => value.includes(c));
        if (forbidHit !== undefined) {
          this.$message.warning(`${this.word}不能包含${this.forbidName || forbidHit}：${value}`);
          return;
        }
        if (seen.has(value)) {
          this.$message.warning(`${this.word}已存在或重复：${value}`);
          return;
        }
        seen.add(value);
      }
      if (this.tagList.length + values.length > this.max) {
        const remaining = Math.max(this.max - this.tagList.length, 0);
        this.$message.warning(`最多添加${this.max}个${this.word}，当前还可添加${remaining}个`);
        return;
      }
      // 末尾的一个分号表示结束，不作为空项提示。
      const checkedParts = this.inputValue.trimEnd().endsWith(";") ? parts.slice(0, -1) : parts;
      const emptyCount = checkedParts.filter((value) => !value).length;
      if (emptyCount) {
        this.$message.warning(`已忽略${emptyCount}个空项`);
      }
      this.tagList.push(...values);
      this.inputValue = "";
      this.syncToParent();
      this.notifyPending("");
    },
    /** 删除标签 */
    handleClose(index) {
      this.tagList.splice(index, 1);
      this.syncToParent();
    },
    /** 拖拽排序结束后同步到父组件 */
    syncToParent() {
      this.$emit("input", [...this.tagList]);
    },
  },
};
</script>

<style scoped>
.tag-input {
  width: 100%;
  line-height: 1;
}
.tag-list {
  display: flex;
  flex-wrap: wrap;
  /* FOUR-2986：标签多时列表内部滚动，避免撑高表单把输入框挤出可视区；
     10-13 个数上限放开至 100 后 200px(约5行)过窄，调至 355px(约9行)——
     日常 40 个标签内全可见，满配 100 个(约24行)才内部滚动 */
  max-height: 355px;
  overflow-y: auto;
}
.tag-item {
  margin: 0 6px 6px 0;
  cursor: grab;
  /* 2026-10-09 设计标注：tag 换企业色系（背景 EBF6F7 / 文字 4FA1A4 / 边框 D2EAEC，
     替代 Element 默认浅蓝 el-tag），仅组件内生效不影响其他页面 el-tag */
  background-color: #ebf6f7 !important;
  color: #4fa1a4 !important;
  border: 1px solid #d2eaec !important;
}
.tag-item:hover {
  border-color: #4fa1a4 !important;
}
.tag-item /deep/ .el-tag__close {
  color: #4fa1a4;
}
.tag-item /deep/ .el-tag__close:hover {
  background-color: #4fa1a4;
  color: #ffffff;
}
.tag-item:active {
  cursor: grabbing;
}
.tag-ghost {
  opacity: 0.5;
}
.tag-editor {
  width: 200px;
}
.tag-editor-batch {
  width: 100%;
}
.tag-list:not(:empty) + .tag-editor-batch {
  margin-top: 8px;
}
.tag-tip {
  margin-top: 2px;
  font-size: 12px;
  color: #999;
}
</style>
